import { useRef, useState } from "react";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { api, getAttribution, trackEvent } from "../../lib/api";
import { TOPIC_BY_ID } from "../../data/contact";
import { ContactFields } from "./ContactFields";

const EMPTY = { naam: "", email: "", telefoon: "", woonplaats: "", bericht: "", callback: false, moment: "geen", website: "" };
const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);

const validate = (f, topic) => {
  const e = {};
  if (f.naam.trim().length < 2) e.naam = "Vul uw naam in";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim())) e.email = "Vul een geldig e-mailadres in";
  const tel = f.telefoon.trim();
  const digits = tel.replace(/\D/g, "");
  if (tel && (!/^[+0-9 ()-]{8,20}$/.test(tel) || digits.length < 9 || digits.length > 15)) e.telefoon = "Vul een geldig telefoonnummer in";
  else if (!tel && f.callback) e.telefoon = "Vul uw telefoonnummer in, dan kunnen we u bellen";
  if (topic === "inmeten" && f.woonplaats.trim().length < 2) e.woonplaats = "Vul uw woonplaats in";
  if (f.bericht.trim().length < 10) e.bericht = "Beschrijf uw vraag in minimaal 10 tekens";
  return e;
};

const apiError = (err) => {
  const d = err?.response?.data?.detail;
  if (err?.response?.status === 429) return "Te veel pogingen achter elkaar. Probeer het over een minuut opnieuw.";
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg.replace(/^Value error, /, "");
  return "Versturen is niet gelukt. Controleer uw verbinding en probeer het opnieuw, of bel ons.";
};

const payload = (f, topic, meta) => {
  const attr = getAttribution();
  const phone = f.telefoon.trim();
  return {
    idempotency_key: meta.key, lead_type: "contact_request", contact_topic: topic, product_category: "anders",
    contact_name: f.naam.trim(), email: f.email.trim(), phone, locality: f.woonplaats.trim(), notes: f.bericht.trim(),
    contact_preference: f.callback ? "telefoon" : phone ? "geen" : "email", contact_moment: f.callback ? f.moment : null,
    consent_privacy: true, marketing_opt_in: false, utm: attr.utm || {}, referrer: attr.referrer || "", landing_page: attr.landing_page || "",
    source_page: "/contact", website: f.website, elapsed_ms: Date.now() - meta.startedAt,
  };
};

const Done = ({ done, onReset }) => (
  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 sm:p-8" role="status" data-testid="contact-success">
    <CheckCircle2 className="h-8 w-8 text-emerald-600" aria-hidden="true" />
    <h3 className="mt-3 text-[20px] font-semibold text-ink">Bedankt, uw bericht is verstuurd</h3>
    <p className="mt-2 text-[15px] leading-relaxed text-slate-700">Uw bericht is bij ons binnen onder nummer <strong data-testid="contact-success-number">{done.lead_number}</strong>. U ontvangt een bevestiging op {done.email}. Wij nemen zo snel mogelijk contact met u op.</p>
    <button type="button" onClick={onReset} className="mt-5 text-[14.5px] font-semibold text-maas hover:underline" data-testid="contact-new-message">Nog een bericht sturen</button>
  </div>
);

// Algemene contactaanvraag → bestaand CRM (/quote-requests, type contactverzoek) met bevestiging en melding per e-mail.
export const ContactForm = ({ topic }) => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const meta = useRef({ key: newKey(), startedAt: Date.now() });
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };
  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(form, topic);
    setErrors(errs);
    if (Object.keys(errs).length || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/quote-requests", payload(form, topic, meta.current));
      if (!data.duplicate_submission) trackEvent("contact_form_submit", { topic });
      setDone({ lead_number: data.lead_number, email: form.email.trim() });
    } catch (err) {
      setErrors({ form: apiError(err) });
    } finally { setBusy(false); }
  };
  const reset = () => { setDone(null); setForm(EMPTY); meta.current = { key: newKey(), startedAt: Date.now() }; };
  if (done) return <Done done={done} onReset={reset} />;
  return (
    <form onSubmit={submit} noValidate className="relative rounded-2xl border border-slate-200 bg-white p-5 sm:p-7" style={{ boxShadow: "var(--shadow-2)" }} data-testid="contact-form">
      <h3 className="text-[19px] font-semibold text-ink" data-testid="contact-form-title">{TOPIC_BY_ID[topic].label}</h3>
      <p className="mt-1 text-[14px] text-slate-500">Velden met * zijn verplicht. Meer hebben we niet nodig.</p>
      <div className="mt-5"><ContactFields topic={topic} form={form} set={set} errors={errors} /></div>
      {errors.form && <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-[14px] font-medium text-red-700" role="alert" data-testid="contact-form-error">{errors.form}</p>}
      <button type="submit" disabled={busy} className="btn-primary mt-6 w-full !min-h-[52px] disabled:opacity-60 sm:w-auto" data-testid="contact-submit">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <Send className="h-5 w-5" aria-hidden="true" />} Bericht versturen
      </button>
    </form>
  );
};
